import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Box,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import imageFileToBase64 from "../../../../../hooks/imageToBase64";

const buttonSx = {
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "10px",
};

const isHttpUrl = (value) => /^https?:\/\//i.test(value);
const isDataUrl = (value) => /^data:image\//i.test(value);

const ImageInsertButton = ({ editor, theme, showToast }) => {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [preview, setPreview] = useState("");
  const [error, setError] = useState("");
  const [previewFailed, setPreviewFailed] = useState(false);
  const [uploading, setUploading] = useState(false);

  const reset = () => {
    setUrl("");
    setPreview("");
    setError("");
    setPreviewFailed(false);
    setUploading(false);
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setUploading(true);
    setError("");
    setPreviewFailed(false);
    try {
      const base64 = await imageFileToBase64(file, 800);
      setPreview(base64);
      setUrl("");
    } catch (err) {
      console.error(err);
      const message = err.message || "Unable to process image.";
      setError(message);
      showToast?.(message, "error");
    } finally {
      setUploading(false);
    }
  };

  const handlePreview = () => {
    const trimmed = url.trim();
    if (!trimmed) {
      setError("Enter an image URL.");
      setPreview("");
      return;
    }
    if (!isHttpUrl(trimmed)) {
      setError("URL must start with http:// or https://");
      setPreview("");
      return;
    }
    setError("");
    setPreviewFailed(false);
    setPreview(trimmed);
  };

  const handleInsert = () => {
    const source = (preview || url).trim();
    if (!editor || !source) {
      setError("Upload an image or preview a URL first.");
      return;
    }
    if (!isHttpUrl(source) && !isDataUrl(source)) {
      setError("Choose a valid image file or URL.");
      return;
    }
    editor.chain().focus().setImage({ src: source }).run();
    handleClose();
  };

  return (
    <>
      <Tooltip title="Add image">
        <IconButton
          size="small"
          onClick={() => setOpen(true)}
          sx={{
            borderRadius: "8px",
            color: "text.secondary",
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <ImageIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "15px",
            boxShadow: "1px 5px 5px 1px rgba(0,0,0,0.18)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>Add image</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload an image from your device, or paste a hosted image URL.
          </Typography>

          <Stack spacing={2}>
            <Box
              sx={{
                p: 2,
                borderRadius: "12px",
                border: "1px dashed",
                borderColor: "divider",
                bgcolor: "#fafafa",
                textAlign: "center",
              }}
            >
              <CloudUploadOutlinedIcon color="action" sx={{ mb: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Upload from device
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1.5 }}
              >
                Images are resized to a max of 800×800 before inserting.
              </Typography>
              <Button
                variant="outlined"
                component="label"
                disabled={uploading}
                theme={theme}
                startIcon={<CloudUploadOutlinedIcon />}
                sx={buttonSx}
              >
                {uploading ? "Processing..." : "Choose image"}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </Button>
            </Box>

            <Divider>
              <Typography variant="caption" color="text.secondary">
                or
              </Typography>
            </Divider>

            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <LinkOutlinedIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Image URL
                </Typography>
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="https://example.com/image.png"
                  value={url}
                  onChange={(e) => {
                    setUrl(e.target.value);
                    if (error) setError("");
                    if (preview && isDataUrl(preview)) {
                      // Keep uploaded preview until user previews a URL.
                    } else if (preview) {
                      setPreview("");
                    }
                    setPreviewFailed(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handlePreview();
                    }
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                />
                <Button
                  variant="outlined"
                  onClick={handlePreview}
                  theme={theme}
                  sx={{ ...buttonSx, whiteSpace: "nowrap" }}
                >
                  Preview
                </Button>
              </Stack>
            </Box>

            {error ? (
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            ) : null}

            {preview ? (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: "12px",
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "#fff",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  Preview
                </Typography>
                {previewFailed ? (
                  <Typography variant="body2" color="error">
                    Could not load this image. Check the file or URL and try again.
                  </Typography>
                ) : (
                  <Box
                    component="img"
                    src={preview}
                    alt="Preview"
                    onError={() => setPreviewFailed(true)}
                    sx={{
                      maxWidth: "100%",
                      maxHeight: 220,
                      objectFit: "contain",
                      borderRadius: "8px",
                    }}
                  />
                )}
              </Box>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} color="inherit" sx={buttonSx} theme={theme}>
            Cancel
          </Button>
          <Button
            onClick={handleInsert}
            variant="contained"
            disabled={!preview || previewFailed || uploading}
            theme={theme}
            sx={buttonSx}
          >
            Insert image
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ImageInsertButton;
